import { Component, OnInit } from '@angular/core';
import { forkJoin } from 'rxjs';
import { StateService } from '@uirouter/angular';
import { NotificationsDataService } from '../../data-access/notifications-data.service';
import { NotificationSort, NotificationWatch } from '../../data-access/notifications.types';

@Component({
    selector: 'app-notification-centre-page',
    templateUrl: './notification-centre-page.component.html',
    styleUrls: ['./notification-centre-page.component.scss']
})
export class NotificationCentrePageComponent implements OnInit {
    private readonly watchSortStorageKey = 'notifications.watchList.sort';
    title = 'Project Watches';
    watches: NotificationWatch[] = [];
    isEditing = false;
    isDeleting = false;
    sort: NotificationSort = { sortField: 'lastnotified', sortDesc: true };

    constructor(
        private notificationsData: NotificationsDataService,
        private stateService: StateService
    ) { }

    ngOnInit(): void {
        this.sort = this.loadStoredSort(this.watchSortStorageKey, this.sort);
        this.loadWatchList(this.sort);
    }

    openWatch(watch: NotificationWatch): void {
        this.stateService.go('projectWatch', {
            guid: watch.guid,
            name: watch.name
        });
    }

    loadWatchList(sort?: NotificationSort): void {
        this.notificationsData.getWatches(sort).subscribe((watches) => {
            this.watches = (watches || []).map((watch) => ({
                ...watch,
                validated: true,
                isDeleting: false,
                isEdited: false,
                originalName: watch.name
            }));
        });
    }

    onSortChanged(sort: NotificationSort): void {
        this.sort = sort;
        this.storeSort(this.watchSortStorageKey, this.sort);
        this.loadWatchList(sort);
    }

    private storeSort(key: string, sort: NotificationSort): void {
        try {
            sessionStorage.setItem(key, JSON.stringify(sort));
        } catch {
        }
    }

    private loadStoredSort(key: string, fallback: NotificationSort): NotificationSort {
        try {
            const raw = sessionStorage.getItem(key);
            if (!raw) {
                return fallback;
            }

            const parsed = JSON.parse(raw) as Partial<NotificationSort>;
            if (typeof parsed.sortField !== 'string' || typeof parsed.sortDesc !== 'boolean') {
                return fallback;
            }

            return {
                sortField: parsed.sortField,
                sortDesc: parsed.sortDesc
            };
        } catch {
            return fallback;
        }
    }

    onToggleEnabled(payload: { watch: NotificationWatch; enabled: boolean }): void {
        payload.watch.isEnabled = payload.enabled;
        this.notificationsData.enableWatch(payload.watch).subscribe(() => {
            payload.watch.nextNotification = this.notificationsData.calculateNextNotification(payload.watch) || undefined;
        });
    }

    startEdit(): void {
        this.isDeleting = false;
        this.isEditing = true;
    }

    startDelete(): void {
        this.isEditing = false;
        this.isDeleting = true;
    }

    cancelMode(): void {
        this.isDeleting = false;
        this.isEditing = false;
        this.watches.forEach((watch) => {
            watch.isDeleting = false;
            watch.isEdited = false;
            watch.name = watch.originalName || watch.name;
            watch.validated = true;
        });
    }

    onWatchChanged(watch: NotificationWatch): void {
        watch.name = (watch.name || '').replace(/\s{2,}/g, ' ');
        watch.isEdited = (watch.name || '') !== (watch.originalName || '');
        watch.validated = true;
    }

    saveChanges(): void {
        const valid = this.notificationsData.validateWatches(this.watches);
        if (!valid) {
            return;
        }

        const updates = this.watches.filter((watch) => watch.isEdited).map((watch) => this.notificationsData.updateWatch(watch));

        if (updates.length === 0) {
            this.isEditing = false;
            return;
        }

        forkJoin(updates).subscribe(() => {
            this.isEditing = false;
            this.loadWatchList(this.sort);
        });
    }

    onToggleDeleteSelection(watch: NotificationWatch): void {
        watch.isDeleting = !watch.isDeleting;
    }

    get hasSelectedDeletes(): boolean {
        return this.watches.some((watch) => !!watch.isDeleting);
    }

    deleteSelected(): void {
        if (!this.hasSelectedDeletes) {
            return;
        }

        const confirmed = window.confirm('This action will delete all marked watches. Are you sure?');
        if (!confirmed) {
            return;
        }

        const deletes = this.watches
            .filter((watch) => !!watch.isDeleting)
            .map((watch) => this.notificationsData.deleteWatch(watch.guid));

        forkJoin(deletes).subscribe(() => {
            this.isDeleting = false;
            this.loadWatchList(this.sort);
        });
    }
}
